/* FLM — Pulse feed: stories rail + posts + poll teaser; story viewer.
   Pixel port of the canvas artboard ScrFeed ("Posts & stories", screens-social.jsx).
   Mint rgba(180,255,145,x) → orange rgba(255,212,145,x). */
import React from 'react';
import { Icon, GradPhoto, Avatar, MonoLabel, RoundBtn, FullOverlay } from '../components/ui';
import { FLM_DATA } from '../data/mock';
import { isDemo } from '../lib/demo';
import { StatePanel, ListSkeleton } from '../lib/feedback';
import { getFeed, getStories, createPost, togglePostLike, uploadStory, mediaUrl } from '../api/client';

/* ---- live feed wiring (data source only; UI untouched) ---- */
const GRADS = [
  ['#2D0041', '#7A1FA2'], ['#4B006E', '#1a0026'], ['#3a0a54', '#12001c'],
  ['#5e1287', '#240035'], ['#1f0030', '#4B006E'], ['#141414', '#2D0041'],
];
const hashOf = (s) => [...String(s)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);
/* Relative age like the mock "2h" (minutes → hours → days). */
const relTime = (iso) => {
  const m = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return m + 'm';
  const h = Math.floor(m / 60);
  return h < 24 ? h + 'h' : Math.floor(h / 24) + 'd';
};
/** Map an API author card to the prototype profile shape the feed renders. */
function authorToCard(a) {
  const h = hashOf(a.userId);
  return {
    id: a.userId,
    userId: a.userId, // marks the card as live (mock cards have no userId)
    name: a.name,
    job: a.job || '',
    verified: Boolean(a.verified),
    online: Boolean(a.online),
    grad: GRADS[h % GRADS.length],
    photoUrl: mediaUrl(a.photoUrl),
  };
}
/** Map an API post to the mock post shape (+ embedded author for resolution). */
function apiToPost(p) {
  return {
    id: p.id,
    by: p.author.userId,
    time: relTime(p.createdAt),
    grad: GRADS[hashOf(p.id) % GRADS.length],
    likes: p.likeCount,
    liked: p.likedByMe,
    text: p.text,
    author: authorToCard(p.author),
    live: true,
  };
}

/* FLM wordmark + accent dot (canvas Wordmark) */
function Wordmark() {
  return (
    <span className="flm-display" style={{ fontSize: 19, display: 'flex', alignItems: 'baseline', gap: 3, color: 'var(--text)' }}>
      FLM
      <i style={{ fontStyle: 'normal', width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 10px var(--accent)', display: 'inline-block' }}></i>
    </span>
  );
}

/* filled verified badge (canvas Verified → tokens.css .flm-verif) */
function Verif({ size = 17 }) {
  return (
    <span className="flm-verif" style={{ width: size, height: size }}>
      <Icon name="check" size={size * 0.58} strokeWidth={3} />
    </span>
  );
}

/* caption with accent hashtags (canvas: "#lisbon" in accent) */
function Caption({ text }) {
  return (
    <p style={{ margin: 0, padding: '0 14px 14px', fontFamily: 'var(--font-body)', fontSize: '0.92rem', color: 'var(--text-2)', lineHeight: 1.5 }}>
      {text.split(/(#[^\s#]+)/).map((part, i) =>
        part.startsWith('#') ? <span key={i} style={{ color: 'var(--accent)' }}>{part}</span> : part)}
    </p>
  );
}

function StoryViewer({ profileId, t, faNum, onClose }) {
  const D = FLM_DATA;
  /* live rail entries are objects ({ p, media, time }); mock entries are profile ids */
  const live = profileId && typeof profileId === 'object' ? profileId : null;
  const p = live ? live.p : D.byId(profileId);
  if (!p) return null;
  return (
    <FullOverlay open={true} z={85}>
      <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} data-screen-label="Story viewer">
        <GradPhoto profile={p} photo={live ? live.media : undefined} initialSize="13rem" style={{ position: 'absolute', inset: 0 }}>
          <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.55), transparent 25%, transparent 70%, rgba(0,0,0,0.7))' }}></span>
        </GradPhoto>
        {/* progress */}
        <div style={{ position: 'absolute', top: 10, insetInline: 14, display: 'flex', gap: 4 }}>
          <span style={{ flex: 1, height: 2.5, borderRadius: 2, background: 'var(--accent)', animation: 'flmProgress 5s linear' }}></span>
          <span style={{ flex: 1, height: 2.5, borderRadius: 2, background: 'rgba(255,255,255,0.25)' }}></span>
        </div>
        <div style={{ position: 'absolute', top: 22, insetInlineStart: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar profile={p} photo={p.photoUrl} size={38} />
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#fff', fontSize: '1rem' }}>{p.name}</span>
          <MonoLabel color="var(--text-secondary)">{(live ? live.time : '2h').toUpperCase()}</MonoLabel>
        </div>
        <div style={{ position: 'absolute', bottom: 20, insetInline: 16, display: 'flex', gap: 10 }} onClick={(e) => e.stopPropagation()}>
          <input className="flm-input" placeholder={t('reply') + '…'} style={{ flex: 1, borderRadius: 'var(--radius-pill)', background: 'rgba(10,10,10,0.6)', backdropFilter: 'blur(10px)' }} />
          <RoundBtn icon="heart" size={46} iconSize={20} color="var(--accent)" bg="rgba(10,10,10,0.6)" />
        </div>
      </div>
    </FullOverlay>
  );
}

function FeedScreen({ t, faNum, onOpenProfile }) {
  const demo = isDemo();
  const D = FLM_DATA;
  const [likes, setLikes] = React.useState({});
  const [story, setStory] = React.useState(null);
  const me = { name: t('you'), grad: ['#7A1FA2', '#2D0041'] };
  const poll = demo ? D.byId('p1') : null;

  // demo: the artboard set · live: null = loading, [] = designed empty state
  const [posts, setPosts] = React.useState(() => (demo ? D.posts : null));
  const [feedErr, setFeedErr] = React.useState(false);
  const [rail, setRail] = React.useState(() => (demo ? D.stories : []));
  const storyInput = React.useRef(null);
  const loadFeed = React.useCallback(() => {
    if (demo) return;
    setFeedErr(false);
    getFeed()
      .then((res) => setPosts((res?.items ?? []).map(apiToPost)))
      .catch(() => { setFeedErr(true); setPosts((p) => p ?? []); });
  }, [demo]);
  const loadStories = React.useCallback(() => {
    if (demo) return;
    getStories()
      .then((res) => {
        setRail((res?.items ?? []).map((g) => {
          const latest = g.stories[g.stories.length - 1];
          return { id: g.author.userId, p: authorToCard(g.author), media: mediaUrl(latest.url), time: relTime(latest.createdAt), live: true };
        }));
      })
      .catch(() => {}); // stories rail stays empty — the feed error panel covers messaging
  }, [demo]);
  React.useEffect(() => { loadFeed(); loadStories(); }, [loadFeed, loadStories]);

  const loading = !demo && posts === null && !feedErr;
  const shown = posts ?? [];

  const toggleLike = (post, liked) => {
    setLikes((l) => ({ ...l, [post.id]: !liked })); // optimistic either way
    if (post.live) togglePostLike(post.id).catch(() => {});
  };
  const compose = () => {
    const text = (window.prompt(t('shareMoment')) || '').trim();
    if (!text) return;
    createPost(text.slice(0, 500)).then(loadFeed).catch(() => {});
  };
  const onStoryFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    uploadStory(file).then(loadStories).catch(() => {});
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, position: 'relative' }} data-screen-label="Pulse feed">
      <span className="radial-glow is-purple" style={{ width: 220, height: 220, top: 200, insetInlineEnd: -90 }}></span>

      {/* top row: wordmark + new post */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 18px 6px', position: 'relative', zIndex: 5, flex: 'none' }}>
        <Wordmark />
        <RoundBtn icon="plus" size={34} iconSize={17} onClick={compose} />
      </div>

      <div className="flm-scroll" style={{ position: 'relative', zIndex: 2 }}>
        {/* stories rail — "you" with plus badge, live rings on the rest */}
        <div className="flm-hscroll" style={{ gap: 12, paddingBottom: 12 }}>
          <input ref={storyInput} type="file" accept="image/*" style={{ display: 'none' }} onChange={onStoryFile} />
          <div style={{ textAlign: 'center', flex: 'none' }} onClick={() => storyInput.current && storyInput.current.click()}>
            <span style={{ display: 'inline-block', position: 'relative', padding: 3, borderRadius: '50%', border: '1px solid var(--border-bright)' }}>
              <GradPhoto profile={me} initialSize="20px" style={{ width: 56, height: 56, borderRadius: '50%', border: '3px solid #000' }} />
              <span style={{ position: 'absolute', bottom: 0, insetInlineEnd: 0, width: 19, height: 19, borderRadius: '50%', background: 'var(--accent)', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #000' }}>
                <Icon name="plus" size={10} strokeWidth={2.5} />
              </span>
            </span>
            <span style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.72rem', color: 'var(--text-2)', marginTop: 2 }}>{t('you')}</span>
          </div>
          {rail.map((entry) => {
            const id = entry && entry.live ? entry.id : entry;
            const p = entry && entry.live ? entry.p : D.byId(id);
            return (
              <div key={id} style={{ textAlign: 'center', flex: 'none', cursor: 'pointer' }} onClick={() => setStory(entry)}>
                <span style={{ display: 'inline-block', padding: 3, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), var(--primary-light))' }}>
                  <GradPhoto profile={p} photo={p.photoUrl} initialSize="20px" style={{ width: 56, height: 56, borderRadius: '50%', border: '3px solid #000' }} />
                </span>
                <span style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.72rem', color: 'var(--text-2)', marginTop: 2 }}>{p.name}</span>
              </div>
            );
          })}
        </div>

        {/* posts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 18px 24px', position: 'relative' }}>
          {loading ? (
            <ListSkeleton rows={4} style={{ padding: 0 }} />
          ) : !demo && feedErr && shown.length === 0 ? (
            <StatePanel title={t('errTitle')} note={t('errNote')} actionLabel={t('retry')} onAction={loadFeed} style={{ minHeight: 260 }} />
          ) : shown.length === 0 ? (
            <StatePanel title={t('feedEmptyTitle')} note={t('feedEmptyNote')} actionLabel={t('shareMoment')} onAction={compose} style={{ minHeight: 260 }} />
          ) : null}
          {shown.map((post, idx) => {
            const p = post.author || D.byId(post.by);
            const liked = likes[post.id] != null ? likes[post.id] : post.liked;
            const count = post.likes + (liked && !post.liked ? 1 : 0) - (!liked && post.liked ? 1 : 0);
            const comments = demo ? Math.max(2, Math.round(post.likes * 0.08)) : null;
            return (
              <React.Fragment key={post.id}>
                <article className="flm-enter" style={{ '--i': Math.min(idx, 8), background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
                  {/* head */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
                    <Avatar profile={p} photo={p.photoUrl} size={38} onClick={() => onOpenProfile(p)} />
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <b style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text)' }}>{p.name}</b>
                        {p.verified ? <Verif size={13} /> : null}
                      </div>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.74rem', color: 'var(--text-3)' }}>{p.job ? `${p.job} · ` : ''}{faNum(post.time)}</span>
                    </div>
                    <Icon name="moreH" size={18} color="var(--text-3)" />
                  </div>
                  {/* full-bleed photo */}
                  <div style={{ height: 240, position: 'relative' }}>
                    <GradPhoto profile={{ name: '◎', grad: post.grad }} initialSize="7rem" style={{ position: 'absolute', inset: 0 }} />
                  </div>
                  {/* actions: love heart · comments · tip TON */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '11px 14px' }}>
                    <button className="flm-ghost-btn" onClick={() => toggleLike(post, liked)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, color: liked ? 'var(--love)' : 'var(--text-2)', padding: 0 }}>
                      <Icon name="heart" size={18} fill={liked ? 'var(--love)' : 'none'} strokeWidth={liked ? 0 : 1.5} />
                      <b style={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem' }}>{faNum(count)}</b>
                    </button>
                    {comments != null ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-2)' }}>
                        <Icon name="message" size={18} />
                        <b style={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem' }}>{faNum(comments)}</b>
                      </span>
                    ) : null}
                    <span style={{ flex: 1 }}></span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ton)', cursor: 'pointer' }}>
                      <Icon name="gift" size={18} />
                      <span className="flm-mono" style={{ fontSize: 9, color: 'var(--ton)' }}>{t('tipTon')}</span>
                    </span>
                  </div>
                  <Caption text={post.text} />
                </article>

                {/* poll teaser card after the first post (demo-only artboard garnish) */}
                {idx === 0 && poll ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow-card)', cursor: 'pointer' }}>
                    <Avatar profile={poll} size={38} />
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <b style={{ fontFamily: 'var(--font-body)', fontSize: '0.9rem', color: 'var(--text)' }}>{t('pollTitle', { name: poll.name })}</b>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('pollSub')}</span>
                    </div>
                    <span className="flm-flip" style={{ display: 'flex', color: 'var(--text-3)' }}><Icon name="chevRight" size={16} /></span>
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {story ? <StoryViewer profileId={story} t={t} faNum={faNum} onClose={() => setStory(null)} /> : null}
    </div>
  );
}

export { FeedScreen, StoryViewer };
